import { useState, useEffect } from 'react';
import { motion, fadeIn, slideInFromBottom, staggerContainer, staggerItem } from '../lib/motion-utils';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  Heart, 
  PhoneOff, 
  Shield, 
  Eye, 
  Smile, 
  Sparkles, 
  MessageCircle, 
  Sunrise, 
  Moon, 
  CheckCircle2, 
  Circle,
  Pause,
  TrendingUp,
  Flame,
} from 'lucide-react';
import { 
  getTodaysMicroActions, 
  completeMicroAction, 
  getMicroActionStats,
  readMicroActionsStatus,
  MicroAction,
} from '../lib/micro-actions';
import { toast } from 'sonner';

const iconMap: Record<string, any> = {
  Heart,
  PhoneOff,
  Shield,
  Eye,
  Smile,
  Sparkles,
  MessageCircle,
  Sunrise,
  Moon,
  Pause,
};

export function MicroActions() {
  const [actions, setActions] = useState<MicroAction[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, completionRate: 0, streak: 0 });
  const [expanded, setExpanded] = useState(true);
  const [status, setStatus] = useState<{
    source: 'atlas' | 'local';
    reason?: string;
    updatedAt: number;
  } | null>(null);

  useEffect(() => {
    loadActions();
    const onUpdate = () => loadActions();
    window.addEventListener('tension-micro-actions-updated', onUpdate);
    return () => window.removeEventListener('tension-micro-actions-updated', onUpdate);
  }, []);

  const loadActions = () => {
    const todaysActions = getTodaysMicroActions();
    setActions(todaysActions);
    setStats(getMicroActionStats());
    setStatus(readMicroActionsStatus());
  };

  const handleComplete = (actionId: string) => {
    completeMicroAction(actionId);
    loadActions();
    
    const action = actions.find(a => a.id === actionId);
    if (action) {
      toast.success('Micro-action completed! 🎉', {
        description: 'Every small step strengthens your awareness.',
      });
    }
  };

  if (actions.length === 0) {
    return null;
  }

  const pendingActions = actions.filter(a => !a.completed);
  const completedActions = actions.filter(a => a.completed);
  const fromAtlas = status?.source === 'atlas' || actions.some((a) => a.id.startsWith('atlas-'));
  const fallbackReasonMap: Record<string, string> = {
    no_token: 'Not signed in',
    network: 'Network issue',
    api_error: 'Atlas unavailable',
    empty_response: 'No Atlas suggestions',
    unknown: 'Fallback mode',
  };

  return (
    <Card className="p-6 border-l-4 border-l-purple-500">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Flame className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-neutral-900 dark:text-neutral-100">Today&apos;s micro-actions</h3>
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  Active coaching • {pendingActions.length} pending
                </p>
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                    fromAtlas
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : 'border-amber-300 bg-amber-50 text-amber-700'
                  }`}
                  title={
                    fromAtlas
                      ? 'Personalized by Atlas'
                      : `Using local fallback${
                          status?.reason ? `: ${fallbackReasonMap[status.reason] || 'offline mode'}` : ''
                        }`
                  }
                >
                  {fromAtlas ? 'Atlas live' : 'Local fallback'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Stats Badge */}
          {stats.streak > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-orange-50 rounded-full">
              <Flame className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-900">{stats.streak} day streak</span>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-neutral-600">Progress</span>
            <span className="font-medium text-neutral-900">
              {stats.completed} / {stats.total}
            </span>
          </div>
          <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500"
              style={{ width: `${stats.completionRate}%` }}
            />
          </div>
        </div>

        {/* Actions List */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-3"
        >
          {pendingActions.map((action) => {
            const Icon = iconMap[action.icon] || Heart;
            
            return (
              <motion.div
                key={action.id}
                variants={staggerItem}
                className={`
                  p-4 rounded-lg border-2 transition-all
                  ${action.rtiState === 'High Tension' ? 'border-red-200 bg-red-50/50' :
                    action.rtiState === 'Elastic' ? 'border-amber-200 bg-amber-50/50' :
                    'border-green-200 bg-green-50/50'}
                `}
              >
                <div className="flex items-start gap-3">
                  <div className={`
                    p-2 rounded-lg
                    ${action.rtiState === 'High Tension' ? 'bg-red-100' :
                      action.rtiState === 'Elastic' ? 'bg-amber-100' :
                      'bg-green-100'}
                  `}>
                    <Icon className={`
                      w-4 h-4
                      ${action.rtiState === 'High Tension' ? 'text-red-600' :
                        action.rtiState === 'Elastic' ? 'text-amber-600' :
                        'text-green-600'}
                    `} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-medium text-neutral-900 text-sm">
                        {action.title}
                      </h4>
                      <span className="text-xs text-neutral-500 whitespace-nowrap">
                        {action.duration}
                      </span>
                    </div>
                    
                    <p className="text-sm text-neutral-600 mt-1 leading-relaxed">
                      {action.description}
                    </p>
                    
                    <div className="flex items-center gap-2 mt-3">
                      <Button
                        onClick={() => handleComplete(action.id)}
                        size="sm"
                        className="h-8 text-xs"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                        Mark Complete
                      </Button>
                      
                      <span className="text-xs text-neutral-500">
                        {action.context}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Completed Actions (Collapsed) */}
          {completedActions.length > 0 && (
            <div className="pt-2">
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-neutral-500 hover:text-neutral-700 transition-colors flex items-center gap-1"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {completedActions.length} completed today
              </button>
              
              {expanded && (
                <div className="mt-2 space-y-2">
                  {completedActions.map((action) => {
                    const Icon = iconMap[action.icon] || Heart;
                    
                    return (
                      <div
                        key={action.id}
                        className="p-3 rounded-lg bg-neutral-50 border border-neutral-200 opacity-60"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                          <span className="text-sm text-neutral-700 line-through">
                            {action.title}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* All Complete State */}
        {pendingActions.length === 0 && completedActions.length > 0 && (
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full">
              <Sparkles className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">
                All micro-actions complete!
              </span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}